import * as React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AdsClickIcon from "@mui/icons-material/AdsClick";
import CallSplitIcon from "@mui/icons-material/CallSplit";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";

import type { KitchenArrivalRow, ShotNumberOverlay, Vec2, Vec3 } from "./RallyBoardPopup";

type Team01 = 0 | 1;
export type OpportunityType = "middle_seam" | "away_space";
type OpportunityView = "for" | "against";
export type OpportunityOutcome = "found" | "missed" | "partial";
type OpportunitySignal = "taken" | "close" | "decisive" | "review";

export type ShotOpportunityCandidate = {
  id: string;
  row: KitchenArrivalRow;
  shotNumber: number;
  shotCount: number;
  playerId: number | null;
  againstPlayerIds: number[];
  teamResult: "won" | "lost" | "unknown";
  shotEndedRally: boolean;
  type: OpportunityType;
  outcome: OpportunityOutcome;
  score: number;
  confidence: number;
  target: { x: number; z: number };
  targetLabel: string;
  reason: string;
  scoreReason: string;
  actualDistanceFt: number | null;
};

export type ShotOpportunityLabContext = {
  title: string;
  subtitle: string;
  shotNumber: number;
  reason: string;
  target: { x: number; z: number };
  type: OpportunityType;
  outcome: OpportunityOutcome;
  teamResult: "won" | "lost" | "unknown";
  shotEndedRally: boolean;
  score: number;
};

function toShotUniverseTarget(target: { x: number; z: number }) {
  return {
    x: target.x - 10,
    z: target.z - 22,
  };
}

export function shotOpportunityLabContext(candidate: ShotOpportunityCandidate): ShotOpportunityLabContext {
  const playerLabel = candidate.playerId == null ? "unknown player" : `p${candidate.playerId}`;
  return {
    title: `${candidate.targetLabel} opportunity`,
    subtitle: `Rally ${candidate.row.rallyIndex + 1} · Shot ${candidate.shotNumber} · ${playerLabel} · score ${candidate.score}`,
    shotNumber: candidate.shotNumber,
    reason: `${candidate.reason} ${candidate.scoreReason} Actual endpoint was ${fmtFt(candidate.actualDistanceFt)} from the nominal target point.`,
    target: toShotUniverseTarget(candidate.target),
    type: candidate.type,
    outcome: candidate.outcome,
    teamResult: candidate.teamResult,
    shotEndedRally: candidate.shotEndedRally,
    score: candidate.score,
  };
}

function clamp01(v: number) {
  return Math.max(0, Math.min(1, v));
}

function distance2(a: { x: number; y: number }, b: { x: number; y: number }) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

function distanceTarget(a: { x: number; z: number }, b: { x: number; z: number }) {
  return Math.hypot(a.x - b.x, a.z - b.z);
}

function fmtFt(v: number | null) {
  return v == null || !Number.isFinite(v) ? "-" : `${v.toFixed(1)} ft`;
}

function overlayTimeMs(overlay: ShotNumberOverlay): number {
  const candidates = [
    overlay.atMsRel,
    overlay.trajectoryStart?.ms,
    overlay.rawShot?.resulting_ball_movement?.trajectory?.start?.ms,
    overlay.rawShot?.start_ms,
  ];
  for (const candidate of candidates) {
    const n = Number(candidate);
    if (Number.isFinite(n)) return n;
  }
  return NaN;
}

function getEndTarget(overlay: ShotNumberOverlay): { x: number; z: number } | null {
  const loc = overlay.trajectoryEnd?.location;
  if (!loc) return null;
  const x = Number((loc as Vec3).x);
  const z = Number((loc as any).y ?? (loc as Vec3).z);
  if (!Number.isFinite(x) || !Number.isFinite(z)) return null;
  return { x, z };
}

function getStartTarget(overlay: ShotNumberOverlay): { x: number; z: number } | null {
  const loc = overlay.trajectoryStart?.location;
  if (!loc) return null;
  const x = Number((loc as Vec3).x);
  const z = Number((loc as any).y ?? (loc as Vec3).z);
  if (!Number.isFinite(x) || !Number.isFinite(z)) return null;
  return { x, z };
}

function middleLaneOutcome(args: {
  actual: { x: number; z: number } | null;
  target: { x: number; z: number };
  opponents: Array<{ playerId: number; pos: Vec2 }>;
}): { outcome: OpportunityOutcome; distanceFt: number | null; lateralMissFt: number | null } {
  if (!args.actual || args.opponents.length < 2) {
    return { outcome: "partial", distanceFt: null, lateralMissFt: null };
  }
  const xs = args.opponents.map((entry) => entry.pos.x).sort((a, b) => a - b);
  const minX = xs[0] ?? args.target.x;
  const maxX = xs[xs.length - 1] ?? args.target.x;
  const seamCenterX = (minX + maxX) / 2;
  const halfGap = Math.max(0, (maxX - minX) / 2);
  const lateralMiss = Math.max(0, Math.abs(args.actual.x - seamCenterX) - halfGap);
  const distanceFt = distanceTarget(args.actual, args.target);
  if (lateralMiss <= 1.5) return { outcome: "found", distanceFt, lateralMissFt: lateralMiss };
  if (lateralMiss <= 3.5) return { outcome: "partial", distanceFt, lateralMissFt: lateralMiss };
  return { outcome: "missed", distanceFt, lateralMissFt: lateralMiss };
}

function teamOpponentPlayers(
  overlay: ShotNumberOverlay,
  playerIdToTeam: (playerId: number | null) => Team01 | null,
) {
  const hitterTeam = playerIdToTeam(overlay.playerId);
  if (hitterTeam == null) return [];
  const positions = overlay.player_positions ?? overlay.rawShot?.player_positions ?? [];
  return positions
    .map((pos, playerId) => ({ playerId, pos }))
    .filter((entry): entry is { playerId: number; pos: Vec2 } => {
      if (!entry.pos) return false;
      return playerIdToTeam(entry.playerId) !== hitterTeam;
    });
}

function hitterTeamResult(
  row: KitchenArrivalRow,
  overlay: ShotNumberOverlay,
  playerIdToTeam: (playerId: number | null) => Team01 | null,
): "won" | "lost" | "unknown" {
  const hitterTeam = playerIdToTeam(overlay.playerId);
  if (hitterTeam == null || row.winnerTeam == null) return "unknown";
  return hitterTeam === row.winnerTeam ? "won" : "lost";
}

function shotEndedRally(overlay: ShotNumberOverlay, shotCount: number) {
  return overlay.rawShot?.is_final === true || overlay.shotNumber >= shotCount;
}

function outcomeForActualTarget(
  actual: { x: number; z: number } | null,
  target: { x: number; z: number },
): { outcome: OpportunityOutcome; distanceFt: number | null } {
  if (!actual) return { outcome: "partial", distanceFt: null };
  const d = distanceTarget(actual, target);
  if (d <= 3.0) return { outcome: "found", distanceFt: d };
  if (d <= 7.0) return { outcome: "partial", distanceFt: d };
  return { outcome: "missed", distanceFt: d };
}

function buildMiddleSeamCandidate(args: {
  row: KitchenArrivalRow;
  overlay: ShotNumberOverlay;
  opponents: Array<{ playerId: number; pos: Vec2 }>;
  shotCount: number;
}): ShotOpportunityCandidate | null {
  if (args.opponents.length < 2) return null;
  const [a, b] = args.opponents;
  if (!a || !b) return null;
  const gap = distance2(a.pos, b.pos);
  if (!Number.isFinite(gap) || gap < 7.5) return null;

  const target = {
    x: (a.pos.x + b.pos.x) / 2,
    z: (a.pos.y + b.pos.y) / 2,
  };
  const actual = getEndTarget(args.overlay);
  const actualMatch = middleLaneOutcome({ actual, target, opponents: args.opponents });
  const gapScore = clamp01((gap - 6.5) / 6.5);
  const outcomeBoost =
    actualMatch.outcome === "found" ? 0.28 : actualMatch.outcome === "partial" ? 0.1 : 0;
  const winnerBoost = args.overlay.winnerType ? 0.14 : 0;
  const slowControlBoost =
    Number(args.overlay.rawShot?.resulting_ball_movement?.speed) <= 14 ? 0.06 : 0;
  const lateRallyBoost =
    args.shotCount >= 8 && args.overlay.shotNumber >= args.shotCount - 2 ? 0.07 : 0;
  const score = Math.round(
    100 *
      clamp01(
        0.44 +
          gapScore * 0.30 +
          outcomeBoost +
          winnerBoost +
          slowControlBoost +
          lateRallyBoost,
      ),
  );
  if (score < 56) return null;

  return {
    id: `${args.row.rallyIndex}:${args.overlay.shotNumber}:middle`,
    row: args.row,
    shotNumber: args.overlay.shotNumber,
    shotCount: args.shotCount,
    playerId: args.overlay.playerId,
    againstPlayerIds: [a.playerId, b.playerId],
    teamResult: "unknown",
    shotEndedRally: shotEndedRally(args.overlay, args.shotCount),
    type: "middle_seam",
    outcome: actualMatch.outcome,
    score,
    confidence: Math.round(100 * clamp01(0.54 + gapScore * 0.28)),
    target,
    targetLabel: "Middle seam",
    reason: `Opponents are separated by ${gap.toFixed(1)} ft; the shot lane runs through the middle seam.`,
    scoreReason: `Score includes separation (${gap.toFixed(1)} ft), actual ${
      actualMatch.outcome === "found"
        ? "reached the seam"
        : actualMatch.outcome === "partial"
          ? "came near the seam"
          : "missed the seam"
    }${args.overlay.winnerType ? ", winner shot factor" : ""}${
      slowControlBoost > 0 ? ", controlled-pace factor" : ""
    }${
      lateRallyBoost > 0 ? ", late-rally context (last two shots of an 8+ shot rally)" : ""
    }.`,
    actualDistanceFt: actualMatch.distanceFt,
  };
}

function buildAwaySpaceCandidate(args: {
  row: KitchenArrivalRow;
  overlay: ShotNumberOverlay;
  opponents: Array<{ playerId: number; pos: Vec2 }>;
  shotCount: number;
}): ShotOpportunityCandidate | null {
  if (!args.opponents.length) return null;
  const avgX =
    args.opponents.reduce((sum, entry) => sum + entry.pos.x, 0) / args.opponents.length;
  const avgZ =
    args.opponents.reduce((sum, entry) => sum + entry.pos.y, 0) / args.opponents.length;
  const targetX = avgX >= 0 ? -7.2 : 7.2;
  const targetZ = avgZ >= 0 ? -11.5 : 11.5;
  const target = { x: targetX, z: targetZ };
  const nearest = Math.min(
    ...args.opponents.map((entry) => distanceTarget({ x: entry.pos.x, z: entry.pos.y }, target)),
  );
  if (!Number.isFinite(nearest) || nearest < 11.5) return null;

  const actual = getEndTarget(args.overlay);
  const actualMatch = outcomeForActualTarget(actual, target);
  const opponentGap =
    args.opponents.length >= 2 ? distance2(args.opponents[0]!.pos, args.opponents[1]!.pos) : 0;
  const actualSeamMatch = middleLaneOutcome({
    actual,
    target: { x: avgX, z: avgZ },
    opponents: args.opponents,
  });
  if (actualMatch.outcome === "missed" && opponentGap >= 7.5 && actualSeamMatch.outcome === "found") {
    return null;
  }
  const start = getStartTarget(args.overlay);
  const shotToTargetDistanceFt = start ? distanceTarget(start, target) : null;
  const lateralFromContactFt = start ? Math.abs(start.x - target.x) : null;
  const feasibilityPenalty =
    shotToTargetDistanceFt == null
      ? 0.08
      : shotToTargetDistanceFt > 38
        ? 0.24
        : shotToTargetDistanceFt > 30
          ? 0.14
          : shotToTargetDistanceFt > 24
            ? 0.06
            : 0;
  const stretchPenalty =
    lateralFromContactFt == null
      ? 0
      : lateralFromContactFt > 18
        ? 0.12
        : lateralFromContactFt > 14
          ? 0.06
          : 0;
  const coverageScore = clamp01((nearest - 11.5) / 10);
  const outcomeBoost =
    actualMatch.outcome === "found" ? 0.18 : actualMatch.outcome === "partial" ? 0.06 : 0;
  const lateRallyBoost =
    args.shotCount >= 8 && args.overlay.shotNumber >= args.shotCount - 2 ? 0.05 : 0;
  const score = Math.round(
    100 *
      clamp01(
        0.46 +
          coverageScore * 0.36 +
          outcomeBoost +
          lateRallyBoost -
          feasibilityPenalty -
          stretchPenalty,
      ),
  );
  if (score < 56) return null;

  return {
    id: `${args.row.rallyIndex}:${args.overlay.shotNumber}:away`,
    row: args.row,
    shotNumber: args.overlay.shotNumber,
    shotCount: args.shotCount,
    playerId: args.overlay.playerId,
    againstPlayerIds: args.opponents.map((entry) => entry.playerId),
    teamResult: "unknown",
    shotEndedRally: shotEndedRally(args.overlay, args.shotCount),
    type: "away_space",
    outcome: actualMatch.outcome,
    score,
    confidence: Math.round(
      100 * clamp01(0.48 + coverageScore * 0.32 - feasibilityPenalty * 0.6 - stretchPenalty),
    ),
    target,
    targetLabel: targetX < 0 ? "Open left/deep space" : "Open right/deep space",
    reason: `Nearest opponent is ${nearest.toFixed(1)} ft from the suggested target zone${
      shotToTargetDistanceFt != null && shotToTargetDistanceFt > 24
        ? `; long path from contact (${shotToTargetDistanceFt.toFixed(1)} ft)`
        : ""
    }${
      lateralFromContactFt != null && lateralFromContactFt > 14
        ? `; large lateral change (${lateralFromContactFt.toFixed(1)} ft)`
        : ""
    }.`,
    scoreReason: `Score includes nearest-opponent space (${nearest.toFixed(1)} ft), actual ${
      actualMatch.outcome === "found"
        ? "reached the zone"
        : actualMatch.outcome === "partial"
          ? "came near the zone"
          : "missed the zone"
    }${
      lateRallyBoost > 0 ? ", late-rally context (last two shots of an 8+ shot rally)" : ""
    }${
      feasibilityPenalty > 0 ? ", long-path penalty" : ""
    }${stretchPenalty > 0 ? ", lateral-change penalty" : ""}.`,
    actualDistanceFt: actualMatch.distanceFt,
  };
}

export function computeShotOpportunityCandidates(args: {
  rows: KitchenArrivalRow[];
  playerIdToTeam: (playerId: number | null) => Team01 | null;
}): ShotOpportunityCandidate[] {
  const out: ShotOpportunityCandidate[] = [];
  for (const row of args.rows) {
    const overlays = [...(row.shotNumberOverlays ?? [])]
      .filter((overlay) => typeof overlay.playerId === "number")
      .sort((a, b) => overlayTimeMs(a) - overlayTimeMs(b));
    const shotCount = overlays.length;
    for (const overlay of overlays) {
      if (overlay.shotNumber <= 2) continue;
      const opponents = teamOpponentPlayers(overlay, args.playerIdToTeam);
      const teamResult = hitterTeamResult(row, overlay, args.playerIdToTeam);
      const middle = buildMiddleSeamCandidate({ row, overlay, opponents, shotCount });
      if (middle) out.push({ ...middle, teamResult });
      const away = buildAwaySpaceCandidate({ row, overlay, opponents, shotCount });
      if (away) out.push({ ...away, teamResult });
    }
  }
  return out
    .sort((a, b) => b.score - a.score || a.row.rallyIndex - b.row.rallyIndex)
    .slice(0, 120);
}

function OpportunityChip(props: { children: React.ReactNode; active?: boolean; onClick?: () => void }) {
  return (
    <Chip
      clickable={!!props.onClick}
      onClick={props.onClick}
      label={props.children}
      size="small"
      variant={props.active ? "filled" : "outlined"}
      sx={{
        color: "rgba(255,255,255,0.9)",
        borderColor: props.active ? "rgba(255,255,255,0.55)" : "rgba(255,255,255,0.22)",
        bgcolor: props.active ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.04)",
        fontWeight: 800,
      }}
    />
  );
}

function opportunitySignal(candidate: ShotOpportunityCandidate): OpportunitySignal {
  if (candidate.outcome === "found") return "taken";
  if (candidate.outcome === "partial") return "close";
  const lateRally =
    candidate.shotCount >= 8 && candidate.shotNumber >= candidate.shotCount - 2;
  if (candidate.teamResult !== "unknown" && lateRally) return "decisive";
  return "review";
}

function opportunitySignalLabel(signal: OpportunitySignal) {
  if (signal === "taken") return "Taken";
  if (signal === "close") return "Close";
  if (signal === "decisive") return "Decisive";
  return "Review";
}

function opportunitySignalColor(signal: OpportunitySignal) {
  if (signal === "taken") return "rgba(74, 170, 103, 0.56)";
  if (signal === "close") return "rgba(225, 180, 72, 0.48)";
  if (signal === "decisive") return "rgba(127, 199, 255, 0.42)";
  return "rgba(255,255,255,0.12)";
}

function opportunitySignalTitle(signal: OpportunitySignal) {
  if (signal === "taken") return "Actual shot reached the suggested area.";
  if (signal === "close") return "Actual shot was near the suggested area.";
  if (signal === "decisive") return "Late-rally opportunity connected to the rally outcome.";
  return "Review-worthy position pattern.";
}

function resultTimingLabel(candidate: ShotOpportunityCandidate) {
  if (candidate.teamResult === "unknown") return "unknown";
  return candidate.shotEndedRally ? "on shot" : "later";
}

function resultEvidenceLabel(candidate: ShotOpportunityCandidate) {
  if (candidate.teamResult === "unknown") return "unknown";
  return `${candidate.teamResult} ${resultTimingLabel(candidate)}`;
}

function resultTimingTitle(candidate: ShotOpportunityCandidate) {
  if (candidate.teamResult === "unknown") return "Rally result is unknown.";
  return candidate.shotEndedRally
    ? `The hitter's team ${candidate.teamResult} the rally on this shot.`
    : `The hitter's team eventually ${candidate.teamResult} the rally after this shot.`;
}

export function OpportunityFinderPopup(props: {
  open: boolean;
  onClose: () => void;
  rows: KitchenArrivalRow[];
  playerIdToTeam: (playerId: number | null) => Team01 | null;
  onOpenShotLab: (
    row: KitchenArrivalRow,
    shotNumber: number,
    context: ShotOpportunityLabContext,
  ) => void;
}) {
  const { open, onClose, rows, playerIdToTeam, onOpenShotLab } = props;
  const [playerFilter, setPlayerFilter] = React.useState<number | "all">(0);
  const [view, setView] = React.useState<OpportunityView>("for");
  const [typeFilter, setTypeFilter] = React.useState<OpportunityType | "all">("all");
  const [outcomeFilter, setOutcomeFilter] = React.useState<OpportunityOutcome | "all">("all");
  const [signalFilter, setSignalFilter] = React.useState<OpportunitySignal | "all">("all");
  const [highSignalOnly, setHighSignalOnly] = React.useState(true);
  const [query, setQuery] = React.useState("");

  const candidates = React.useMemo(
    () => computeShotOpportunityCandidates({ rows, playerIdToTeam }),
    [playerIdToTeam, rows],
  );

  const filtered = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const queryParts = q.split(/\s+/).filter(Boolean);
    return candidates.filter((candidate) => {
      if (playerFilter !== "all") {
        if (view === "for" && candidate.playerId !== playerFilter) return false;
        if (view === "against" && !candidate.againstPlayerIds.includes(playerFilter)) {
          return false;
        }
      }
      if (typeFilter !== "all" && candidate.type !== typeFilter) return false;
      if (outcomeFilter !== "all" && candidate.outcome !== outcomeFilter) return false;
      const signal = opportunitySignal(candidate);
      if (signalFilter !== "all" && signal !== signalFilter) return false;
      if (queryParts.length) {
        const searchable = [
          `r${candidate.row.rallyIndex + 1}`,
          `rally ${candidate.row.rallyIndex + 1}`,
          `s${candidate.shotNumber}`,
          `shot ${candidate.shotNumber}`,
          candidate.playerId == null ? "unknown" : `p${candidate.playerId}`,
          candidate.targetLabel,
          candidate.outcome,
          candidate.teamResult,
          resultTimingLabel(candidate),
          resultEvidenceLabel(candidate),
          candidate.shotEndedRally ? "on this shot terminal final shot" : "later in rally eventual result",
          opportunitySignalLabel(signal),
          candidate.reason,
          String(candidate.score),
        ]
          .join(" ")
          .toLowerCase();
        if (!queryParts.every((part) => searchable.includes(part))) return false;
      }
      if (highSignalOnly && !queryParts.length) {
        if (signal === "review" && candidate.score < 78) return false;
        if (candidate.confidence < 52 && candidate.outcome === "missed") return false;
      }
      return true;
    });
  }, [candidates, highSignalOnly, outcomeFilter, playerFilter, query, signalFilter, typeFilter, view]);

  const filteredSignalCounts = React.useMemo(() => {
    const counts: Record<OpportunitySignal, number> = {
      taken: 0,
      close: 0,
      decisive: 0,
      review: 0,
    };
    for (const candidate of filtered) {
      counts[opportunitySignal(candidate)] += 1;
    }
    return counts;
  }, [filtered]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      data-happy-path="opportunity-finder-popup"
      PaperProps={{
        sx: {
          width: "min(1280px, 96vw)",
          height: "min(780px, 92vh)",
          bgcolor: "rgba(12,12,12,0.98)",
          color: "rgba(255,255,255,0.92)",
          border: "1px solid rgba(255,255,255,0.18)",
          borderRadius: 1,
          boxShadow: "0 18px 70px rgba(0,0,0,0.50)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 1.5,
          py: 1,
          display: "flex",
          alignItems: "center",
          gap: 1,
          borderBottom: "1px solid rgba(255,255,255,0.12)",
        }}
      >
        <AdsClickIcon sx={{ color: "#7fc7ff" }} />
        <Box sx={{ fontSize: 18, fontWeight: 950 }}>Opportunity Finder</Box>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose} sx={{ color: "rgba(255,255,255,0.72)" }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0, display: "flex", minHeight: 0 }}>
        <Box
          sx={{
            flex: "0 0 260px",
            p: 1.25,
            borderRight: "1px solid rgba(255,255,255,0.10)",
            bgcolor: "rgba(255,255,255,0.025)",
            display: "flex",
            flexDirection: "column",
            gap: 1.15,
          }}
        >
          <Box sx={{ fontSize: 12, color: "rgba(255,255,255,0.62)", lineHeight: 1.35 }}>
            Position-based candidates only. These are review prompts, not claims about paddle
            readiness or intent.
          </Box>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 0.55,
              color: "rgba(255,255,255,0.72)",
              fontSize: 11,
              fontWeight: 850,
            }}
          >
            {(["taken", "close", "decisive", "review"] as const).map((signal) => (
              <Box
                key={signal}
                component="button"
                type="button"
                onClick={() =>
                  setSignalFilter((current) => (current === signal ? "all" : signal))
                }
                sx={{
                  px: 0.7,
                  py: 0.45,
                  borderRadius: 1,
                  bgcolor:
                    signalFilter === signal ? opportunitySignalColor(signal) : "rgba(255,255,255,0.055)",
                  border:
                    signalFilter === signal
                      ? "1px solid rgba(255,255,255,0.34)"
                      : "1px solid rgba(255,255,255,0.08)",
                  color: "rgba(255,255,255,0.82)",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 0.5,
                  font: "inherit",
                  textAlign: "left",
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.28)",
                    bgcolor:
                      signalFilter === signal
                        ? opportunitySignalColor(signal)
                        : "rgba(255,255,255,0.09)",
                  },
                }}
              >
                <Box component="span">{opportunitySignalLabel(signal)}</Box>
                <Box component="span" sx={{ fontVariantNumeric: "tabular-nums" }}>
                  {filteredSignalCounts[signal]}
                </Box>
              </Box>
            ))}
          </Box>
          <Box sx={{ display: "flex", gap: 0.7, flexWrap: "wrap" }}>
            <OpportunityChip active={view === "for"} onClick={() => setView("for")}>
              For player
            </OpportunityChip>
            <OpportunityChip active={view === "against"} onClick={() => setView("against")}>
              Against player
            </OpportunityChip>
          </Box>
          <Select
            value={playerFilter}
            size="small"
            onChange={(event) => {
              const value = event.target.value;
              setPlayerFilter(value === "all" ? "all" : Number(value));
            }}
            sx={{
              color: "white",
              ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.22)" },
              ".MuiSvgIcon-root": { color: "rgba(255,255,255,0.72)" },
            }}
          >
            <MenuItem value="all">All players</MenuItem>
            {[0, 1, 2, 3].map((playerId) => (
              <MenuItem key={playerId} value={playerId}>
                p{playerId}
              </MenuItem>
            ))}
          </Select>
          <TextField
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Find R24, S19, middle, won..."
            size="small"
            inputProps={{
              "aria-label": "Find opportunities",
              "data-happy-path": "opportunity-finder-search",
            }}
            sx={{
              input: { color: "white", fontWeight: 800, fontSize: 13 },
              ".MuiOutlinedInput-root": {
                bgcolor: "rgba(255,255,255,0.04)",
              },
              ".MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.22)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.34)",
              },
            }}
          />
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7 }}>
            <OpportunityChip active={typeFilter === "all"} onClick={() => setTypeFilter("all")}>
              All targets
            </OpportunityChip>
            <OpportunityChip
              active={typeFilter === "middle_seam"}
              onClick={() => setTypeFilter("middle_seam")}
            >
              Middle seam
            </OpportunityChip>
            <OpportunityChip
              active={typeFilter === "away_space"}
              onClick={() => setTypeFilter("away_space")}
            >
              Away space
            </OpportunityChip>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7 }}>
            <OpportunityChip
              active={highSignalOnly}
              onClick={() => setHighSignalOnly((current) => !current)}
            >
              Strong findings
            </OpportunityChip>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.7 }}>
            {(["all", "found", "partial", "missed"] as const).map((value) => (
              <OpportunityChip
                key={value}
                active={outcomeFilter === value}
                onClick={() => setOutcomeFilter(value)}
              >
                {value === "all" ? "All outcomes" : value}
              </OpportunityChip>
            ))}
          </Box>
          <Box sx={{ mt: "auto", fontSize: 12, color: "rgba(255,255,255,0.62)" }}>
            Showing {filtered.length} of {candidates.length} candidates.
          </Box>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
          <Box
            sx={{
              px: 1.2,
              py: 0.75,
              display: "grid",
              gridTemplateColumns: "122px 86px 62px 118px 74px 70px 112px 60px 1fr",
              gap: 1,
              borderBottom: "1px solid rgba(255,255,255,0.10)",
              color: "rgba(255,255,255,0.62)",
              fontSize: 11,
              fontWeight: 900,
              textTransform: "uppercase",
            }}
          >
            <Box />
            <Box>Moment</Box>
            <Box>Player</Box>
            <Box>Opportunity</Box>
            <Box>Finding</Box>
            <Box>Outcome</Box>
            <Box>Result timing</Box>
            <Box>Score</Box>
            <Box>Why</Box>
          </Box>
          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>
            {filtered.length ? (
              filtered.map((candidate) => {
                const signal = opportunitySignal(candidate);
                const isTito =
                  candidate.row.rallyIndex + 1 === 24 &&
                  candidate.shotNumber === 19 &&
                  candidate.playerId === 0;
                return (
                  <Box
                    key={candidate.id}
                    data-happy-path="opportunity-finder-row"
                    data-opportunity-id={candidate.id}
                    data-rally={candidate.row.rallyIndex + 1}
                    data-shot={candidate.shotNumber}
                    data-player={candidate.playerId == null ? "unknown" : `p${candidate.playerId}`}
                    data-outcome={candidate.outcome}
                    data-team-result={candidate.teamResult}
                    data-shot-ended-rally={candidate.shotEndedRally ? "true" : "false"}
                    data-result-timing={resultTimingLabel(candidate)}
                    data-result-evidence={resultEvidenceLabel(candidate)}
                    data-signal={signal}
                    data-score={candidate.score}
                    data-target-label={candidate.targetLabel}
                    data-reason={candidate.reason}
                    data-score-reason={candidate.scoreReason}
                    data-actual-distance-ft={
                      candidate.actualDistanceFt == null ? "" : candidate.actualDistanceFt.toFixed(3)
                    }
                    data-confidence={candidate.confidence}
                    data-is-tito={isTito ? "true" : "false"}
                    sx={{
                      px: 1.2,
                      py: 0.85,
                      display: "grid",
                      gridTemplateColumns: "122px 86px 62px 118px 74px 70px 112px 60px 1fr",
                      gap: 1,
                      alignItems: "center",
                      borderBottom: "1px solid rgba(255,255,255,0.07)",
                      outline: isTito ? "2px solid rgba(246, 198, 91, 0.82)" : undefined,
                      outlineOffset: isTito ? "-2px" : undefined,
                      bgcolor: isTito ? "rgba(246, 198, 91, 0.10)" : undefined,
                      "&:hover": {
                        bgcolor: isTito ? "rgba(246, 198, 91, 0.15)" : "rgba(255,255,255,0.045)",
                      },
                    }}
                  >
                  <Button
                    size="small"
                    variant={isTito ? "contained" : "outlined"}
                    data-happy-path="opportunity-finder-shot-lab"
                    data-rally={candidate.row.rallyIndex + 1}
                    data-shot={candidate.shotNumber}
                    endIcon={<OpenInNewIcon />}
                    onClick={() =>
                      onOpenShotLab(
                        candidate.row,
                        candidate.shotNumber,
                        shotOpportunityLabContext(candidate),
                      )
                    }
                    sx={{
                      textTransform: "none",
                      color: isTito ? "#16120a" : "white",
                      bgcolor: isTito ? "#f6c65b" : undefined,
                      borderColor: isTito ? "#f6c65b" : "rgba(255,255,255,0.26)",
                      fontWeight: 850,
                      "&:hover": {
                        bgcolor: isTito ? "#ffd978" : undefined,
                      },
                    }}
                  >
                    Shot Lab
                  </Button>
                  <Box sx={{ fontWeight: 950, fontVariantNumeric: "tabular-nums" }}>
                    R{candidate.row.rallyIndex + 1} · S{candidate.shotNumber}
                  </Box>
                  <Box sx={{ fontWeight: 850 }}>
                    {candidate.playerId == null ? "unknown" : `p${candidate.playerId}`}
                  </Box>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.65 }}>
                    {candidate.type === "middle_seam" ? (
                      <CallSplitIcon sx={{ fontSize: 17, color: "#7fc7ff" }} />
                    ) : (
                      <PersonSearchIcon sx={{ fontSize: 17, color: "#a9df7b" }} />
                    )}
                    <Box sx={{ fontWeight: 850 }}>{candidate.targetLabel}</Box>
                  </Box>
                  <Tooltip title={opportunitySignalTitle(signal)}>
                    <Chip
                      size="small"
                      label={opportunitySignalLabel(signal)}
                      sx={{
                        width: 70,
                        color: "white",
                        fontWeight: 900,
                        bgcolor: opportunitySignalColor(signal),
                      }}
                    />
                  </Tooltip>
                  <Chip
                    size="small"
                    label={candidate.outcome}
                    sx={{
                      width: 74,
                      color: "white",
                      fontWeight: 900,
                      bgcolor:
                        candidate.outcome === "found"
                          ? "rgba(74, 170, 103, 0.55)"
                          : candidate.outcome === "partial"
                            ? "rgba(225, 180, 72, 0.46)"
                            : "rgba(255,255,255,0.10)",
                    }}
                  />
                  <Tooltip title={resultTimingTitle(candidate)}>
                    <Chip
                      size="small"
                      label={resultEvidenceLabel(candidate)}
                      sx={{
                        width: 106,
                        color: "white",
                        fontWeight: 900,
                        bgcolor:
                          candidate.teamResult === "won" && candidate.shotEndedRally
                            ? "rgba(74, 170, 103, 0.52)"
                            : candidate.teamResult === "lost" && candidate.shotEndedRally
                              ? "rgba(237, 94, 94, 0.44)"
                              : candidate.teamResult === "won"
                                ? "rgba(74, 170, 103, 0.28)"
                                : candidate.teamResult === "lost"
                                  ? "rgba(237, 94, 94, 0.26)"
                                  : "rgba(255,255,255,0.10)",
                      }}
                    />
                  </Tooltip>
                  <Tooltip title={`Confidence ${candidate.confidence}%`}>
                    <Box sx={{ fontWeight: 950 }}>{candidate.score}</Box>
                  </Tooltip>
                  <Box sx={{ color: "rgba(255,255,255,0.74)", fontSize: 12.5 }}>
                    {candidate.reason} {candidate.scoreReason} Actual endpoint was{" "}
                    {fmtFt(candidate.actualDistanceFt)} from the nominal target point.
                  </Box>
                  </Box>
                );
              })
            ) : (
              <Box sx={{ p: 2, color: "rgba(255,255,255,0.62)" }}>
                No opportunities match the current filters.
              </Box>
            )}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
